
import { AudioFeatures } from '../types';
import { FFT_SIZE } from '../constants';

export interface PlaybackState {
  currentTime: number;
  duration: number;
  isPlaying: boolean;
  hasBuffer: boolean;
}

export const extractAudioFeatures = (
  freqData: Uint8Array,
  timeData: Uint8Array,
  sampleRate: number,
  fftSize: number,
  prevEnergy: { value: number }
): AudioFeatures => {
  const binSize = sampleRate / fftSize;

  const getBandEnergy = (lowHz: number, highHz: number) => {
    const startBin = Math.max(0, Math.floor(lowHz / binSize));
    const endBin = Math.min(freqData.length - 1, Math.floor(highHz / binSize));
    let sum = 0;
    for (let i = startBin; i <= endBin; i++) {
      sum += freqData[i] || 0;
    }
    return (sum / (endBin - startBin + 1)) / 255;
  };

  let rmsSum = 0;
  let silenceCheck = true;
  for (let i = 0; i < timeData.length; i++) {
    const val = (timeData[i] - 128) / 128;
    if (Math.abs(val) > 0.001) silenceCheck = false;
    rmsSum += val * val;
  }
  const rms = silenceCheck ? 0 : Math.sqrt(rmsSum / timeData.length);

  let weightedSum = 0;
  let totalSum = 0;
  for (let i = 0; i < freqData.length; i++) {
    weightedSum += (freqData[i] || 0) * i * binSize;
    totalSum += freqData[i] || 0;
  }
  const centroid = totalSum > 1 ? (weightedSum / totalSum) / (sampleRate / 2) : 0;

  const currentEnergy = totalSum / freqData.length / 255;
  const transient = Math.max(0, currentEnergy - prevEnergy.value);
  prevEnergy.value = currentEnergy;

  return {
    sub: silenceCheck ? 0 : getBandEnergy(20, 60),
    bass: silenceCheck ? 0 : getBandEnergy(60, 250),
    lowMid: silenceCheck ? 0 : getBandEnergy(250, 1000),
    highMid: silenceCheck ? 0 : getBandEnergy(1000, 4000),
    high: silenceCheck ? 0 : getBandEnergy(4000, 12000),
    rms,
    centroid,
    transient: silenceCheck ? 0 : transient * 10
  };
};

export class AudioAnalyzer {
  private context: AudioContext;
  private analyzer: AnalyserNode;
  private freqData: Uint8Array;
  private timeData: Uint8Array;
  private prevEnergyObj = { value: 0 };
  private source: MediaStreamAudioSourceNode | AudioBufferSourceNode | OscillatorNode | null = null;
  private gainNode: GainNode;
  private testMode: boolean = false;

  // Playback state for files
  private audioBuffer: AudioBuffer | null = null;
  private startTime: number = 0; 
  private offset: number = 0;    
  private isPlaying: boolean = false;

  constructor() {
    this.context = new (window.AudioContext || (window as any).webkitAudioContext)();
    this.analyzer = this.context.createAnalyser();
    this.analyzer.fftSize = FFT_SIZE;
    this.analyzer.smoothingTimeConstant = 0.8; // High stability for deterministic response
    
    this.gainNode = this.context.createGain();
    this.gainNode.connect(this.analyzer);

    this.freqData = new Uint8Array(this.analyzer.frequencyBinCount);
    this.timeData = new Uint8Array(this.analyzer.fftSize);
  }

  public async setTestMode(enabled: boolean) {
    this.testMode = enabled;
    this.stopSource();
    this.audioBuffer = null;
    
    if (enabled) {
      const osc = this.context.createOscillator();
      const lfo = this.context.createOscillator();
      const lfoGain = this.context.createGain();
      
      osc.type = 'sine';
      lfo.type = 'sine';
      lfo.frequency.value = 0.5;
      lfoGain.gain.value = 200;
      
      lfo.connect(lfoGain);
      lfoGain.connect(osc.frequency);
      
      osc.connect(this.gainNode);
      osc.start();
      lfo.start();
      this.source = osc;
    }
    
    if (this.context.state === 'suspended') await this.context.resume();
  }

  public async setStream(stream: MediaStream) {
    this.testMode = false;
    this.stopSource();
    this.audioBuffer = null;
    const sourceNode = this.context.createMediaStreamSource(stream);
    sourceNode.connect(this.gainNode);
    this.source = sourceNode;
    if (this.context.state === 'suspended') await this.context.resume();
  }

  public async setFile(buffer: AudioBuffer) {
    this.testMode = false;
    this.stopSource();
    this.audioBuffer = buffer;
    this.offset = 0;
    this.play();
    if (this.context.state === 'suspended') await this.context.resume();
  }

  private stopSource() {
    if (this.source instanceof AudioBufferSourceNode || this.source instanceof OscillatorNode) {
      try { this.source.stop(); } catch(e) {}
    }
    if (this.source) {
      this.source.disconnect();
      this.source = null;
    }
    this.isPlaying = false;
  }

  public play() {
    if (this.isPlaying) return;
    
    if (this.audioBuffer) {
      this.stopSource();
      const sourceNode = this.context.createBufferSource();
      sourceNode.buffer = this.audioBuffer;
      sourceNode.connect(this.gainNode);
      this.gainNode.connect(this.context.destination);
      
      sourceNode.start(0, this.offset);
      this.source = sourceNode;
      
      sourceNode.onended = () => {
        if (this.isPlaying && this.source === sourceNode) {
          this.isPlaying = false;
          this.offset = this.audioBuffer ? this.audioBuffer.duration : 0;
        }
      };
    }
    
    this.startTime = this.context.currentTime;
    this.isPlaying = true;
  }

  public pause() {
    if (!this.isPlaying) return;
    this.offset = this.getCurrentTime();
    if (this.audioBuffer) {
      this.stopSource();
    } else {
      this.isPlaying = false;
    }
  }

  public seek(time: number) {
    const wasPlaying = this.isPlaying;
    const duration = this.audioBuffer ? this.audioBuffer.duration : 10; // 10s virtual duration
    this.offset = Math.max(0, Math.min(time, duration));
    
    if (wasPlaying) {
      if (this.audioBuffer) {
        this.stopSource();
        this.play();
      } else {
        this.startTime = this.context.currentTime;
      }
    }
  }

  public getCurrentTime(): number {
    if (!this.isPlaying) return this.offset;
    const elapsed = this.context.currentTime - this.startTime;
    const duration = this.audioBuffer ? this.audioBuffer.duration : 10;
    
    let time = this.offset + elapsed;
    if (!this.audioBuffer && time > duration) {
      // Loop virtual timeline
      time = time % duration;
      this.offset = time;
      this.startTime = this.context.currentTime;
    }
    return Math.min(time, duration);
  }

  public getPlaybackState(): PlaybackState {
    return {
      currentTime: this.getCurrentTime(),
      duration: this.audioBuffer?.duration || 10,
      isPlaying: this.isPlaying,
      hasBuffer: !!this.audioBuffer
    };
  }

  public getFeatures(): AudioFeatures {
    this.analyzer.getByteFrequencyData(this.freqData as any);
    this.analyzer.getByteTimeDomainData(this.timeData as any);

    return extractAudioFeatures(
      this.freqData,
      this.timeData,
      this.context.sampleRate,
      FFT_SIZE,
      this.prevEnergyObj
    );
  }

  public getBuffer(): AudioBuffer | null {
    return this.audioBuffer;
  }

  public getAudioContext() { return this.context; }
  public getAnalyzer() { return this.analyzer; }

  public close() {
    this.context.close();
  }
}
